import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementPart, RHProcurementPartOffer } from "../../../../models/prodModels"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHFont, RHColors } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartView } from "../RHProcurementPartView"
import { populatedValuesOfReferences } from "../../Core/RHReference"
import type { RHFileItemData } from "../../Custom components/RH/RHFileItemView"


/**
 * One white card per procurement part on the OFFERER side, mirroring the
 * procurer's per-part evaluation card (RHPartOfferEvaluationSection): a reused
 * RHProcurementPartView header (identical to the procurer's), the offer price,
 * and the offer's documents listed with the same compact "Teabevahetus"
 * attachment visual.
 *
 * The card owns only the shared top section. Each consuming view supplies its
 * own context-specific footer via `footerViews` (e.g. the "Olete esitanud"
 * banner + action buttons on the submitted view, or the evaluation result
 * banner + private messaging on the result view).
 */
export class OfferPartCardView extends RHPanelView {

    // Reused, identical to the procurer's part header (blue left border).
    readonly partView = new RHProcurementPartView()
        .addedAsSubviewToView(this) as RHProcurementPartView

    // "Pakkumuse hind (KM-ta): <b>X €</b>" — same font size as the existing offerer
    // preview, but without the grey price panel around it.
    readonly priceLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColors.textBootstrap.stringValue,
                fontSize: RHFont.sizeMedium
            }
        })
        .addedAsSubviewToView(this)

    // Added/removed depending on whether the offer carries a comment.
    readonly commentsLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: { color: RHColor.textMuted.stringValue, fontSize: RHFont.sizeSmall }
        })

    readonly requiredDocumentsSectionLabel = new UITextView()
        .configuredWithObject({
            text: "Nõutud dokumendid",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColor.dark.stringValue,
                fontSize: RHFont.sizeMedium,
                fontWeight: "700"
            }
        })

    readonly requiredDocumentsListView = new RHFileListView()
        .configuredWithObject({
            variant: RHFileListView.variant.compactDownload
        }) as RHFileListView

    readonly documentsSectionLabel = new UITextView()
        .configuredWithObject({
            text: "Dokumendid",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColor.dark.stringValue,
                fontSize: RHFont.sizeMedium,
                fontWeight: "700"
            }
        })

    readonly documentsListView = new RHFileListView()
        .configuredWithObject({
            variant: RHFileListView.variant.compactDownload
        }) as RHFileListView

    readonly noDocumentsLabel = new UITextView()
        .configuredWithObject({
            text: "Dokumente ei ole lisatud",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: RHFont.sizeSmall, fontStyle: "italic" }
        })

    _footerViews: UIView[] = []

    constructor(elementID?: string) {
        super(elementID)
        // White card so the RHProcurementPartView header (backgroundLight) reads as
        // the darker-toned box — matching the procurer's per-part card exactly.
        this.backgroundColor = UIColor.whiteColor
    }

    // ── Public API ────────────────────────────────────────────────────────────

    configureWithPartOffer(
        part: RHProcurementPart,
        partOffer: RHProcurementPartOffer,
        partCount?: number,
        isClosedWithoutContract: boolean = NO
    ) {

        // Header — identical to the procurer's card.
        this.partView.configureWithPartData(
            part,
            YES,
            undefined,
            partCount,
            isClosedWithoutContract
        )

        // Price — no grey box, value emphasised.
        // _decryptionFailed is set by the server when the sealed-bid envelope could not be decrypted.
        const priceText = partOffer._decryptionFailed
            ? "???"
            : RHEuroFormatter.formattedCurrency(partOffer.price.amount, {
                currency: partOffer.price.currency || "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
        this.priceLabel.text = `${"Pakkumuse hind (KM-ta):"} <b>${priceText}</b>`

        // Comments
        const hasComments = !!partOffer.offererComments?.trim()
        if (hasComments) {
            this.commentsLabel.text = `Kommentaar: ${partOffer.offererComments?.trim()}`
            this.commentsLabel.addedAsSubviewToView(this)
        }
        else {
            this.commentsLabel.removeFromSuperview()
        }

        // Required documents — kept as their own section (only the uploaded ones).
        const requiredDocumentFiles = this._requiredDocumentFiles(part, partOffer)
        if (requiredDocumentFiles.length > 0) {
            this.requiredDocumentsSectionLabel.addedAsSubviewToView(this)
            this.requiredDocumentsListView.files = requiredDocumentFiles
            this.requiredDocumentsListView.configureWithObject({
                downloadEnabled: YES,
                deleteEnabled: NO
            })
            this.requiredDocumentsListView.addedAsSubviewToView(this)
        }
        else {
            this.requiredDocumentsSectionLabel.removeFromSuperview()
            this.requiredDocumentsListView.removeFromSuperview()
        }

        // Documents (the former "Lisafailid") — always shown with its header.
        this.documentsSectionLabel.addedAsSubviewToView(this)
        const additionalFiles = populatedValuesOfReferences(partOffer.files)
        if (additionalFiles.length > 0) {
            this.noDocumentsLabel.removeFromSuperview()
            this.documentsListView.files = additionalFiles
            this.documentsListView.configureWithObject({
                downloadEnabled: YES,
                deleteEnabled: NO
            })
            this.documentsListView.addedAsSubviewToView(this)
        }
        else {
            this.documentsListView.removeFromSuperview()
            this.noDocumentsLabel.addedAsSubviewToView(this)
        }

        this.setNeedsLayoutUpToRootView()
    }

    setFooterViews(footerViews: UIView[]) {
        this._footerViews.forEach(footerView => footerView.removeFromSuperview())
        this._footerViews = footerViews
        this._footerViews.forEach(footerView => footerView.addedAsSubviewToView(this))
        this.setNeedsLayoutUpToRootView()
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _requiredDocumentFiles(part: RHProcurementPart, partOffer: RHProcurementPartOffer): RHFileItemData[] {
        const requiredDocuments = part.requiredDocuments ?? []
        const requiredDocumentFileIds = partOffer.requiredDocumentFileIds ?? {}
        const requiredDocumentFiles: RHFileItemData[] = []
        for (const requiredDocument of requiredDocuments) {
            const fileIds = [requiredDocumentFileIds[requiredDocument._id] ?? []].flat()
            const populatedFiles = [partOffer.requiredDocumentFiles?.[requiredDocument._id] ?? []].flat()
            for (const fileId of fileIds) {
                const populatedFile = populatedFiles.find(file => file._id?.toString() === fileId)
                if (populatedFile) {
                    requiredDocumentFiles.push(populatedFile)
                }
                else {
                    requiredDocumentFiles.push({ _id: fileId, name: requiredDocument.name })
                }
            }
        }
        return requiredDocumentFiles
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)

        let current = bounds
            .rectangleWithHeight(this.partView)
            .assignedAsFrameOfView(this.partView)

        current = current
            .rectangleForNextRow(padding, this.priceLabel)
            .assignedAsFrameOfView(this.priceLabel)

        if (this.commentsLabel.superview === this) {
            current = current
                .rectangleForNextRow(padding * 0.5, this.commentsLabel)
                .assignedAsFrameOfView(this.commentsLabel)
        }

        if (this.requiredDocumentsSectionLabel.superview === this) {
            current = current
                .rectangleForNextRow(padding, this.requiredDocumentsSectionLabel)
                .assignedAsFrameOfView(this.requiredDocumentsSectionLabel)
                .rectangleForNextRow(padding * 0.5, this.requiredDocumentsListView)
                .assignedAsFrameOfView(this.requiredDocumentsListView)
        }

        current = current
            .rectangleForNextRow(padding, this.documentsSectionLabel)
            .assignedAsFrameOfView(this.documentsSectionLabel)

        if (this.documentsListView.superview === this) {
            current = current
                .rectangleForNextRow(padding * 0.5, this.documentsListView)
                .assignedAsFrameOfView(this.documentsListView)
        }
        else {
            current = current
                .rectangleForNextRow(padding * 0.5, this.noDocumentsLabel)
                .assignedAsFrameOfView(this.noDocumentsLabel)
        }

        if (this._footerViews.length > 0) {
            current
                .rectangleForNextRow(padding, 0)
                .framesByDistributingViewsAsColumn(this._footerViews, padding, this._footerViews)
        }
    }

    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.core.paddingLength
        const width = constrainingWidth || this.bounds.width
        const contentWidth = Math.max(0, width - padding * 2)

        let height = padding
        height += this.partView.intrinsicContentHeight(contentWidth)
        height += padding + this.priceLabel.intrinsicContentHeight(contentWidth)

        if (this.commentsLabel.superview === this) {
            height += padding * 0.5 + this.commentsLabel.intrinsicContentHeight(contentWidth)
        }

        if (this.requiredDocumentsSectionLabel.superview === this) {
            height += padding + this.requiredDocumentsSectionLabel.intrinsicContentHeight(contentWidth)
            height += padding * 0.5 + this.requiredDocumentsListView.intrinsicContentHeight(contentWidth)
        }

        height += padding + this.documentsSectionLabel.intrinsicContentHeight(contentWidth)
        if (this.documentsListView.superview === this) {
            height += padding * 0.5 + this.documentsListView.intrinsicContentHeight(contentWidth)
        }
        else {
            height += padding * 0.5 + this.noDocumentsLabel.intrinsicContentHeight(contentWidth)
        }

        for (const footerView of this._footerViews) {
            height += padding + footerView.intrinsicContentHeight(contentWidth)
        }

        return height + padding
    }

}
